import * as React from 'react'

import { useDroppable } from '@dnd-kit/core'
import clsx from 'clsx'
import { getUnixTime } from 'date-fns'

import { ChannelIcon, PlusIcon, UnstyledButton } from '@buffer-mono/popcorn'

import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { getChannelName } from '~publish/helpers/channels'
import { trackEmptyStateClicked } from '~publish/helpers/queueCalendarTracking'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import styles from './QueueSlot.module.css'

type QueueSlotProps = {
  date: string
  channelId: string
  channelService?: Service
  organizationId?: string
  tags: string[]
}

export const QueueSlot = React.forwardRef<HTMLButtonElement, QueueSlotProps>(
  (
    { date, channelId, channelService, organizationId, tags, ...props },
    ref,
  ) => {
    const { triggerAttributes, createNewPinnedPostInComposer } =
      usePostComposer()
    const dateTimeFormatter = useDateTimeFormatter()

    const { setNodeRef, isOver, active } = useDroppable({
      id: date,
      data: { type: 'slot', date },
    })

    const handlePostClick = (
      event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    ): void => {
      event.preventDefault()
      if (channelService && organizationId) {
        trackEmptyStateClicked({
          organizationId,
          feature: 'queue',
          channel: channelService,
        })
      }
      void createNewPinnedPostInComposer({
        cta: 'publish-singleChannel-queue-slot-1',
        channels: [channelId],
        prefillPostData: {
          pinned: true,
          dueAt: getUnixTime(date),
          tags,
        },
      })
    }

    return (
      <UnstyledButton
        {...props}
        {...triggerAttributes}
        className={clsx(
          styles.slot,
          channelService && styles.withChannel,
          isOver && styles.over,
          active?.id && styles.dragInProgress,
        )}
        data-datetime={date}
        data-testid="queue-slot"
        aria-label={
          channelService
            ? `Add a post to ${getChannelName(
                channelService,
              )} at ${dateTimeFormatter(date)}`
            : undefined
        }
        onClick={handlePostClick}
        ref={(node): void => {
          setNodeRef(node)
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
      >
        {channelService && (
          <ChannelIcon
            type={channelService}
            color="branded"
            size={16}
            aria-hidden
          />
        )}
        <PlusIcon /> New
      </UnstyledButton>
    )
  },
)

QueueSlot.displayName = 'QueueSlot'
